import { useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import SharedModal from '../Modal/SharedModal';

//Sources
import AboutSourcesGda from './AboutSections/AboutSourcesGda';
import AboutSourcesVda from './AboutSections/AboutSourcesVda';
import AboutSourcesDda from './AboutSections/AboutSourcesDda.tsx';

//Metrics
import AboutMetricsGdaScore from './AboutSections/Metrics/AboutMetricsGdaScore.tsx';
import AboutMetricsVdaScore from './AboutSections/Metrics/AboutMetricsVdaScore.tsx';
import AboutMetricsSpecificity from './AboutSections/Metrics/AboutMetricsSpecificity.tsx';
import AboutMetricsPleiotropy from './AboutSections/Metrics/AboutMetricsPleiotropy.tsx';
import AboutMetricsEvidenceIndex from './AboutSections/Metrics/AboutMetricsEvidenceIndex.tsx';
import AboutMetricsNormalizedScore from './AboutSections/Metrics/AboutMetricsNormalizedScore.tsx';
import References from './AboutSections/Attributes/References/References';

//Diseases
import DiseaseType from './AboutSections/Attributes/Diseases/DiseaseType';
import DO from './AboutSections/Attributes/Diseases/DO';
import MeSHDiseaseClass from './AboutSections/Attributes/Diseases/MeSHDiseaseClass';
import HPO from './AboutSections/Attributes/Diseases/HPO';

//Gene Disease
import FirstRef from './AboutSections/Attributes/GeneDisease/FirstRef';
import LastRef from './AboutSections/Attributes/GeneDisease/LastRef';
import OriginalDB from './AboutSections/Attributes/GeneDisease/OriginalDB';
import EvidenceLevel from './AboutSections/Attributes/GeneDisease/EvidenceLevel';

//Genes
import GenespLI from './AboutSections/Attributes/Genes/GenespLI';
import GenesProteinClass from './AboutSections/Attributes/Genes/GenesProteinClass';
import GenesUniProt from './AboutSections/Attributes/Genes/GenesUniProt';

//Variants
import Position from './AboutSections/Attributes/Variants/Position';
import Type from './AboutSections/Attributes/Variants/Type';
import RiskAllele from './AboutSections/Attributes/Variants/RiskAllele';
import Consequence from './AboutSections/Attributes/Variants/Consequence';
import Alleles from './AboutSections/Attributes/Variants/Alleles';
import GenomeExome from './AboutSections/Attributes/Variants/Genome-Exome';
import Hgvsc from './AboutSections/Attributes/Variants/Hgvsc';
import Hgvsp from './AboutSections/Attributes/Variants/Hgvsp';

//Variant Disease
import PMID from './AboutSections/Attributes/VarianDiseases/PMID';
import PValue from './AboutSections/Attributes/VarianDiseases/PValue';

export const searchGridHelpSectionIdPrefix = 'search-grid-help-';

function sectionMatchesQuery(searchBlob: string, query: string) {
  const t = searchBlob.toLowerCase();
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const terms = q.split(/\s+/).filter(Boolean);
  return terms.every(term => t.includes(term));
}

function HelpSection({
  sectionId,
  title,
  children,
}: {
  sectionId: string;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      id={`${searchGridHelpSectionIdPrefix}${sectionId}`}
      className="border-b border-slate-200 py-3 last:border-0 first:pt-0 scroll-mt-3"
    >
      <h3 className="text-base font-semibold text-slate-900 mb-2">{title}</h3>
      {children}
    </section>
  );
}

type SectionItem = { sectionId: string; title: ReactNode; searchText: string; content: ReactNode };

function buildSectionItems(): SectionItem[] {
  return [
    {
      sectionId: 'showGDA',
      title: 'Gene-Disease Associations (GDA)',
      searchText: 'gda gene disease association sources gene-disease',
      content: <AboutSourcesGda />,
    },
    {
      sectionId: 'showVDA',
      title: 'Variant-Disease Associations (VDA)',
      searchText: 'vda variant disease association sources',
      content: <AboutSourcesVda />,
    },
    {
      sectionId: 'showDDA',
      title: 'Drug-Disease Associations (DDA)',
      searchText: 'dda drug disease association sources',
      content: <AboutSourcesDda />,
    },
    {
      sectionId: 'showScore',
      title: 'Association score (GDA and VDA)',
      searchText: 'score gda vda metrics association weight',
      content: (
        <>
          <AboutMetricsGdaScore />
          <AboutMetricsVdaScore />
        </>
      ),
    },
    {
      sectionId: 'showDSI',
      title: 'Disease Specificity Index (DSI)',
      searchText: 'dsi disease specificity index',
      content: <AboutMetricsSpecificity />,
    },
    {
      sectionId: 'showDPI',
      title: 'Disease Pleiotropy Index (DPI)',
      searchText: 'dpi disease pleiotropy index',
      content: <AboutMetricsPleiotropy />,
    },
    {
      sectionId: 'showEI',
      title: 'Evidence index',
      searchText: 'ei evidence index',
      content: <AboutMetricsEvidenceIndex />,
    },
    {
      sectionId: 'showDiseaseType',
      title: 'Disease type',
      searchText: 'disease type',
      content: <DiseaseType />,
    },
    {
      sectionId: 'showDO',
      title: 'Disease Ontology (DO)',
      searchText: 'do disease ontology class',
      content: <DO />,
    },
    {
      sectionId: 'showHPO',
      title: 'Human Phenotype Ontology (HPO)',
      searchText: 'hpo human phenotype ontology',
      content: <HPO />,
    },
    {
      sectionId: 'showMeSH',
      title: 'MeSH disease class',
      searchText: 'mesh semantic disease class umls',
      content: <MeSHDiseaseClass />,
    },
    {
      sectionId: 'showpLI',
      title: 'pLI',
      searchText: 'pli loss of function intolerant probability',
      content: <GenespLI />,
    },
    {
      sectionId: 'showProteinClass',
      title: 'Protein classification',
      searchText: 'protein class classification drug target ontology',
      content: <GenesProteinClass />,
    },
    {
      sectionId: 'showUniProt',
      title: 'UniProt',
      searchText: 'uniprot accession protein',
      content: <GenesUniProt />,
    },
    {
      sectionId: 'showVariantPosition',
      title: 'Variant position',
      searchText: 'position chromosome variant coord',
      content: <Position />,
    },
    {
      sectionId: 'showVariantType',
      title: 'Variant type',
      searchText: 'variant type dbsnp class',
      content: <Type />,
    },
    {
      sectionId: 'showVariantRiskAllele',
      title: 'Risk allele',
      searchText: 'risk allele gwas',
      content: <RiskAllele />,
    },
    {
      sectionId: 'showVariantConsequence',
      title: 'Consequence type',
      searchText: 'consequence vep',
      content: <Consequence />,
    },
    {
      sectionId: 'showAlleles',
      title: 'Alleles',
      searchText: 'alleles ref alt reference alternative',
      content: <Alleles />,
    },
    {
      sectionId: 'showGenomeExome',
      title: 'AF genomes - exomes',
      searchText: 'af allele frequency gnomad genome exome',
      content: <GenomeExome />,
    },
    {
      sectionId: 'showHgvsc',
      title: (
        <>
          Hgvs <sub>c</sub>
        </>
      ),
      searchText: 'hgvs hgvsc coding dna cdna transcript',
      content: <Hgvsc />,
    },
    {
      sectionId: 'showHgvsp',
      title: (
        <>
          Hgvs <sub>p</sub>
        </>
      ),
      searchText: 'hgvs hgvsp protein amino acid',
      content: <Hgvsp />,
    },
    {
      sectionId: 'showFirstRef',
      title: 'First reference',
      searchText: 'first ref reference year initial',
      content: <FirstRef />,
    },
    {
      sectionId: 'showLastRef',
      title: 'Last reference',
      searchText: 'last ref reference year final',
      content: <LastRef />,
    },
    {
      sectionId: 'showEvidenceLevel',
      title: 'Evidence level for the GDA',
      searchText: 'evidence level gda el',
      content: <EvidenceLevel />,
    },
    {
      sectionId: 'showOriginalDB',
      title: 'Original database',
      searchText: 'original database source',
      content: <OriginalDB />,
    },
    {
      sectionId: 'showPMID',
      title: 'PubMed identifier',
      searchText: 'pmid pubmed identifier article',
      content: <PMID />,
    },
    {
      sectionId: 'showReferences',
      title: 'References',
      searchText: 'references pmid nctid citation translation normalized score study id timestamp nct title',
      content: (
        <>
          <References showHeading={false} />
          <div className="mt-4 border-t border-slate-200 pt-4">
            <AboutMetricsNormalizedScore />
          </div>
        </>
      ),
    },
    {
      sectionId: 'showpValue',
      title: 'P-value of the GWAS',
      searchText: 'pvalue p-value gwas statistics',
      content: <PValue />,
    },
  ];
}

const SECTION_ITEMS = buildSectionItems();

export function SearchGridHelpContent() {
  const [sectionQuery, setSectionQuery] = useState('');

  const visibleSections = useMemo(() => {
    return SECTION_ITEMS.filter(item =>
      sectionMatchesQuery(`${item.searchText} ${item.sectionId.toLowerCase()}`, sectionQuery),
    );
  }, [sectionQuery]);

  return (
    <div className="flex flex-col gap-0">
      <div className="relative mb-3 shrink-0">
        <MagnifyingGlassIcon
          className="pointer-events-none absolute left-2.5 bottom-3 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden
        />
        <input
          type="search"
          value={sectionQuery}
          onChange={e => setSectionQuery(e.target.value)}
          placeholder="Search sections (e.g. pLI, GDA, PMID…)"
          className="w-full rounded-md border border-slate-300 bg-white py-2 pl-8 mt-2 pr-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500"
          autoComplete="off"
          spellCheck={false}
          aria-label="Filter table reference sections"
        />
      </div>
      <div
        id="search-grid-help-scroller"
        className="overflow-y-auto pr-1 -mr-0.5 text-left"
        style={{ maxHeight: 'min(75vh,650px)' }}
      >
        {visibleSections.length === 0 ? (
          <p className="text-sm text-slate-500 py-2">No sections match your search. Try other keywords.</p>
        ) : (
          visibleSections.map(item => (
            <HelpSection key={item.sectionId} sectionId={item.sectionId} title={item.title}>
              {item.content}
            </HelpSection>
          ))
        )}
      </div>
    </div>
  );
}

type SearchGridHelpModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  scrollToSectionId?: string;
};

export function SearchGridHelpModal({ open, setOpen, scrollToSectionId }: SearchGridHelpModalProps) {
  useLayoutEffect(() => {
    if (!open) return;
    if (!scrollToSectionId) {
      const r = requestAnimationFrame(() => {
        document.getElementById('search-grid-help-scroller')?.scrollTo(0, 0);
      });
      return () => cancelAnimationFrame(r);
    }
    const el = document.getElementById(`${searchGridHelpSectionIdPrefix}${scrollToSectionId}`);
    const t = window.setTimeout(() => {
      el?.scrollIntoView({ block: 'start', inline: 'nearest' });
    }, 0);
    return () => clearTimeout(t);
  }, [open, scrollToSectionId]);

  return (
    <SharedModal open={open} setOpen={setOpen} title="Table reference" content={<SearchGridHelpContent />} />
  );
}

export default SearchGridHelpModal;
